import React, { forwardRef, type Ref } from 'react';

import { AiSidebarProvider } from '@mint-lib/ai-sidebar-circuit';
import { InfiniteData } from '@mint-lib/query-client';
import { ProgressCircular } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';
import type { Suggestions } from '@mint-sdk/api-recommendations';
import { useAtomValue } from 'jotai';

import { selectedSuggestionAtom } from '../../../store/suggestion/selectedSuggestion.js';
import SuggestionCard from '../SuggestionCard/SuggestionCard.jsx';
import SuggestionCardExpandable from '../SuggestionCardExpandable/SuggestionCardExpandable.jsx';

type SuggestionListProps = {
  data:
    | InfiniteData<{
        items: Suggestions.SuggestionResponseType[];
        page: number;
        pages: number;
      }>
    | undefined;
  isFetchingNextPage: boolean;
  hasNextPage: boolean | undefined;
  campaignId?: number;
  handleSelectCard: (suggestion: Suggestions.SuggestionResponseType) => void;
};

const SuggestionList = forwardRef(function SuggestionList(
  {
    data,
    handleSelectCard,
    isFetchingNextPage,
    hasNextPage,
    campaignId,
  }: SuggestionListProps,
  ref,
) {
  const selectedSuggestion = useAtomValue(selectedSuggestionAtom);

  return (
    <>
      {data?.pages.map((el) =>
        el.items.map((suggestion) =>
          campaignId ? (
            <AiSidebarProvider
              sidebarWidth={316}
              shouldMoveSidebar={false}
              key={suggestion.id}
            >
              <SuggestionCardExpandable
                onSelect={handleSelectCard}
                suggestion={suggestion}
              />
            </AiSidebarProvider>
          ) : (
            <SuggestionCard
              key={suggestion.id}
              onSelect={handleSelectCard}
              suggestion={suggestion}
              isSelected={suggestion.id === selectedSuggestion?.id}
            />
          ),
        ),
      )}
      {isFetchingNextPage && (
        <div className="p-2.6 flex flex-row justify-center">
          <ProgressCircular size="small" />
        </div>
      )}

      <div
        ref={ref as Ref<HTMLDivElement> | undefined}
        className={cn(
          'mt-11',
          hasNextPage && !isFetchingNextPage ? 'block' : 'hidden',
        )}
      />
    </>
  );
});

export default SuggestionList;
